import { TextField } from '@quintype/em/components/text-field';
import { Checkbox } from '@quintype/em/components/checkbox';
import React from 'react';
import get from 'lodash/get';
import { useDispatch, useSelector } from 'react-redux';
import { updateHaveUnsavedChanges, updatePageSettings } from '../../actions/builder-action';
import { updateRowsList } from '../../actions/row-action';
import { ToggleButton } from '../toggle-button';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import styles from './subscription.module.css';
import { FieldLabel } from '@quintype/em/components/field-label';
import { ThemeSetter } from '../theme-setter';
import { Separator } from '../../atoms/separator';
import { updateMeterSettings } from '../../actions/builder-action';
import {
  SUBSCRIPTION_LAYOUTS,
  IDataItems,
  getSubscriptionSetting,
  getLoginlessSettings
} from './constants';

export const SubscriptionSettings = () => {
  const dispatch = useDispatch();

  const { subscription = {}, enableDarkModePreview = false } = useSelector(state =>
    get(state, ['builder', 'config'], {})
  );
  const { pageSetting = {}, rows = [] } = subscription;
  const updateSubscription = (key: string, value: string | boolean) => {
    if (key === 'mandatoryEmail' && !value) {
      const isPhoneChecked = Boolean(get(pageSetting, ['mandatoryPhone'], false));
      if (!isPhoneChecked) {
        // Email must be checked if phone number is not checked (neither can be unchecked)
        return;
      }
    } else if (key === 'mandatoryPhone' && !value) {
      const isEmailChecked = Boolean(get(pageSetting, ['mandatoryEmail'], true));
      if (!isEmailChecked) {
        // If unchecking phone leaves email unchecked, email must be checked by default
        dispatch(updatePageSettings({ mandatoryPhone: false, mandatoryEmail: true }));
        dispatch(updateHaveUnsavedChanges(true));
        return;
      }
    }

    dispatch(updatePageSettings({ [key]: value }));

    if (key === 'enableLoginlessSubscription') {
      const subRow = rows.find((r: any) => SUBSCRIPTION_LAYOUTS.includes(r.layout));
      const currentRow = subRow || rows[0] || {};
      const targetLayout = value ? 'LoginlessFlat' : 'Lavish';
      const currentTitle = get(pageSetting, ['pageTitle']);
      const defaultPageTitle = value ? 'Upgrade to Premium' : 'Discover the Power of News Subscription Plans';
      const shouldUpdateTitle =
        currentTitle === undefined ||
        currentTitle === 'Discover the Power of News Subscription Plans' ||
        currentTitle === 'Upgrade to Premium';

      if (shouldUpdateTitle) {
        dispatch(updatePageSettings({ pageTitle: defaultPageTitle }));
      }

      dispatch(
        updateRowsList({
          rowAction: 'update',
          rowActionPayload: {
            selectedRowId: currentRow.rowId || 1573720413476,
            row: {
              ...currentRow,
              rowId: currentRow.rowId || 1573720413476,
              pageType: 'subscription',
              layout: targetLayout,
              layoutLabel: targetLayout
            }
          }
        })
      );

      if (!value) {
        rows.forEach((row: any) => {
          if (
            row.type === 'Ads' ||
            row.type === 'ads' ||
            row.type === 'widget' ||
            !SUBSCRIPTION_LAYOUTS.includes(row.layout)
          ) {
            dispatch(
              updateRowsList({
                rowAction: 'delete_row',
                rowActionPayload: {
                  selectedRowId: row.rowId,
                  row: row || {}
                }
              })
            );
          }
        });
      }
    }

    switch (key) {
      case 'buttonColor':
        dispatch(updateMeterSettings({ key: 'subscribeButtonColor', value }));
        break;
      case 'darkButtonColor':
        dispatch(updateMeterSettings({ key: 'subscribeDarkButtonColor', value }));
        break;
    }

    dispatch(updateHaveUnsavedChanges(true));
  };

  const isLoginlessEnabled = get(pageSetting, ['enableLoginlessSubscription'], false);
  const layout = get(rows, [0, 'layout'], 'Lavish');


  // ─── Shared renderer ─────────────────────────────────────────────────────────

  const renderSetting = (item: IDataItems) => {
    const { type, label, key, defaultValue = '', placeHolder, darkKey = '' } = item || {};

    const currentValue = get(pageSetting, [key], '');
    const placeholderText = placeHolder || (defaultValue as string) || '';

    switch (type) {
      case 'quillEditor': {
        const modules = {
          toolbar: [
            ['bold', 'italic', 'underline', 'strike'],
            [{ list: 'ordered' }, { list: 'bullet' }]
          ]
        };

        const formats = ['bold', 'italic', 'underline', 'strike', 'list', 'bullet'];
        return (
          <div className={styles.quillWrapper} key={key}>
            <FieldLabel label={label} />
            <ReactQuill
              theme="snow"
              modules={modules}
              formats={formats}
              value={currentValue as string}
              onChange={(value: any) => {
                const cleanedValue = value === '<p><br></p>' ? '' : value;
                updateSubscription(key, cleanedValue);
              }}
              placeholder={placeholderText}
            />
          </div>
        );
      }
      case 'textField':
        return (
          <TextField
            key={key}
            label={label}
            placeholder={placeholderText}
            value={currentValue as string}
            onChange={(value: any) => updateSubscription(key, value)}
          />
        );
      case 'toggle':
        return (
          <div className={styles.toggleContainer} key={key}>
            <ToggleButton
              label={label}
              toggleName={key}
              id={key}
              updateToggleState={get(pageSetting, [key], defaultValue)}
              updateToggleButton={(name: string, value: boolean) => updateSubscription(name, value)}
            />
          </div>
        );
      case 'colorPicker':
        return (
          <div className={styles.colorPickerContainer} key={key}>
            <ThemeSetter
              label={label}
              updateTheStore={updateSubscription}
              lightColorExtract={get(pageSetting, [key], defaultValue as string)}
              lightUpdateStoreKey={key}
              darkColorExtract={get(pageSetting, [darkKey], defaultValue as string)}
              darkUpdateStoreKey={darkKey}
              lightDefaultColor="#4860BC"
              darkDefaultColor="#4860BC"
              enableDarkModePreview={enableDarkModePreview}
            />
          </div>
        );
      case 'separator':
        return <Separator key={key} />;
      case 'fieldLabel':
        return (
          <div className={styles.fieldLabel} key={key}>
            <FieldLabel label={label} />
          </div>
        );
      case 'checkbox': {
        const isPhoneChecked = Boolean(get(pageSetting, ['mandatoryPhone'], false));
        let isChecked: boolean;
        if (key === 'mandatoryEmail') {
          isChecked = !isPhoneChecked ? true : Boolean(get(pageSetting, [key], defaultValue));
        } else {
          isChecked = Boolean(get(pageSetting, [key], defaultValue));
        }

        return (
          <div className={styles.checkboxWrapper} key={key}>
            <Checkbox
              label={label}
              id={key}
              checked={isChecked}
              disabled={false}
              onChange={(value: boolean) => updateSubscription(key, value)}
            />
          </div>
        );
      }
      case 'hintText':
        return (
          <p className={styles.hintText} key={key}>
            {label}
          </p>
        );
      default:
        return null;
    }
  };

  const settingsToRender = isLoginlessEnabled ? getLoginlessSettings() : getSubscriptionSetting(layout);

  return (
    <>
      {/* Enable Loginless Subscription — always visible at the top */}
      <div className={styles.toggleContainer}>
        <ToggleButton
          label="Enable Loginless Subscription"
          toggleName="enableLoginlessSubscription"
          id="enableLoginlessSubscription"
          updateToggleState={isLoginlessEnabled}
          updateToggleButton={(name: string, value: boolean) => updateSubscription(name, value)}
        />
      </div>
      {isLoginlessEnabled && (
        <div className={styles.hintText}>
          Loginless Subscription comes with its own dedicated templates. Other templates can&apos;t be used while this
          is enabled.
        </div>
      )}

      {settingsToRender.map((item: IDataItems) => renderSetting(item))}
    </>
  );
};
